/**
 * CSRF Protection Service
 * Provides CSRF token management for state-changing operations
 */

import {
  clearCSRFToken,
  generateCSRFToken,
  getCSRFToken,
  validateCSRFToken,
} from '../utils/security';

export interface CSRFProtectedRequest {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  data?: unknown;
  headers?: Record<string, string>;
}

export class CSRFService {
  private static instance: CSRFService;
  private token: string | null = null;

  private constructor() {
    this.initializeToken();
  }

  public static getInstance(): CSRFService {
    if (!CSRFService.instance) {
      CSRFService.instance = new CSRFService();
    }
    return CSRFService.instance;
  }

  private initializeToken(): void {
    // Try to get existing token or generate new one
    this.token = getCSRFToken() || generateCSRFToken();
  }

  public getToken(): string {
    if (!this.token) {
      this.token = generateCSRFToken();
    }
    return this.token;
  }

  public refreshToken(): string {
    this.token = generateCSRFToken();
    return this.token;
  }

  public validateToken(token: string): boolean {
    return validateCSRFToken(token);
  }

  public clearToken(): void {
    this.token = null;
    clearCSRFToken();
  }

  // Add CSRF token to request headers
  public addCSRFHeader(
    headers: Record<string, string> = {}
  ): Record<string, string> {
    return {
      ...headers,
      'X-CSRF-Token': this.getToken(),
    };
  }

  // Wrapper for fetch requests with CSRF protection
  public async protectedFetch(
    request: CSRFProtectedRequest
  ): Promise<Response> {
    const { url, method, data, headers = {} } = request;

    const protectedHeaders = this.addCSRFHeader(headers);

    const fetchOptions: globalThis.RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...protectedHeaders,
      },
      credentials: 'same-origin', // Include cookies for session-based auth
    };

    if (data) {
      fetchOptions.body = JSON.stringify(data);
    }

    const response = await fetch(url, fetchOptions);

    // If we get a 403 (Forbidden) due to CSRF, refresh token and retry once
    if (response.status === 403) {
      const errorText = await response.text();
      if (errorText.includes('CSRF') || errorText.includes('csrf')) {
        this.refreshToken();

        // Retry with new token
        const retryHeaders = this.addCSRFHeader(headers);
        const retryOptions: globalThis.RequestInit = {
          ...fetchOptions,
          headers: {
            'Content-Type': 'application/json',
            ...retryHeaders,
          },
        };

        return fetch(url, retryOptions);
      }
    }

    return response;
  }
}

// Export singleton instance
export const csrfService = CSRFService.getInstance();

// React hook for CSRF token
export const useCSRFToken = () => {
  const token = csrfService.getToken();

  return {
    token,
    refreshToken: () => csrfService.refreshToken(),
    addToHeaders: (headers?: Record<string, string>) =>
      csrfService.addCSRFHeader(headers),
  };
};
